import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { TextInput, PasswordInput, Button, Stack, Anchor, Alert, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { ResetPasswordCommand } from '../../../application/dto/auth/ResetPasswordCommand';
import { PUBLIC_ROUTES } from '../../routing/routes';
import { AuthLayout } from '../../layouts/AuthLayout';
import { useAuthStore } from '../../../application/store/authStore';

/**
 * Form data interface for password reset
 */
interface ResetPasswordFormData {
  email: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * ResetPasswordPage - Reset password with token from email
 *
 * Features:
 * - Extracts token and email from URL query parameters
 * - New password input with strength validation
 * - Confirm password input
 * - Submit button with loading state
 * - Success message with redirect to login
 * - Uses authStore for state management
 */
export const ResetPasswordPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword: resetPasswordAction } = useAuthStore();

  // Local state for form handling
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  // Extract token and email from URL query params
  const token = searchParams.get('token') || '';
  const emailFromUrl = searchParams.get('email') || '';

  // Mantine form with validation
  const form = useForm<ResetPasswordFormData>({
    initialValues: {
      email: emailFromUrl,
      newPassword: '',
      confirmPassword: ''
    },

    validate: {
      email: (value: string): string | null => {
        if (!value || value.trim().length === 0) {
          return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Invalid email format';
        }
        return null;
      },
      newPassword: (value: string): string | null => {
        if (!value || value.trim().length === 0) {
          return 'Password is required';
        }
        if (value.length < 8) {
          return 'Password must be at least 8 characters';
        }
        if (!/[A-Z]/.test(value)) {
          return 'Password must contain at least one uppercase letter';
        }
        if (!/[a-z]/.test(value)) {
          return 'Password must contain at least one lowercase letter';
        }
        if (!/[0-9]/.test(value)) {
          return 'Password must contain at least one number';
        }
        return null;
      },
      confirmPassword: (value: string, values: ResetPasswordFormData): string | null => {
        if (value !== values.newPassword) {
          return 'Passwords do not match';
        }
        return null;
      }
    }
  });

  /**
   * Update email field when URL param changes
   */
  useEffect(() => {
    if (emailFromUrl) {
      form.setFieldValue('email', emailFromUrl);
    }
  }, [emailFromUrl, form]);

  /**
   * Check if token is present
   */
  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset.');
    }
  }, [token]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: ResetPasswordFormData): Promise<void> => {
    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const command: ResetPasswordCommand = {
        email: values.email,
        token: token,
        newPassword: values.newPassword
      };

      await resetPasswordAction(command);
      setSuccess(true);
      form.reset();

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate(PUBLIC_ROUTES.LOGIN);
      }, 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout title="Reset Password" subtitle="Enter your new password">
      {/* Error Alert */}
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" variant="light">
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert icon={<IconCheck size={16} />} title="Password Reset Successful" color="green" variant="light">
          Your password has been reset successfully. Redirecting to login...
        </Alert>
      )}

      {/* Reset Password Form */}
      {!success && token && (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {/* Email Input */}
            <TextInput
              label="Email"
              placeholder="your@email.com"
              type="email"
              required
              {...form.getInputProps('email')}
            />

            {/* New Password Input */}
            <PasswordInput
              label="New Password"
              placeholder="Your new password"
              required
              description="Must be at least 8 characters with uppercase, lowercase, and number"
              {...form.getInputProps('newPassword')}
            />

            {/* Confirm Password Input */}
            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm your new password"
              required
              {...form.getInputProps('confirmPassword')}
            />

            {/* Submit Button */}
            <Button type="submit" fullWidth loading={isLoading} mt="md">
              Reset Password
            </Button>
          </Stack>
        </form>
      )}

      {/* Back to Login Link */}
      <Text ta="center" size="sm" mt="md">
        <Anchor component={Link} to={PUBLIC_ROUTES.LOGIN} fw={600}>
          Back to login
        </Anchor>
      </Text>
    </AuthLayout>
  );
};
